import React, { useEffect, useRef, useState } from 'react';
import { useAdminApiService } from '../../hooks/useAdminApiService';
import Trading212AccountsSection from './Trading212AccountsSection';
import './AdminComponents.css';

type Provider = 'KUCOIN' | 'TRADING212';

interface PersonalAccountSummary {
  id: number;
  name: string;
  apiType: string;
  baseUrl?: string;
  isActive: boolean;
  apiKeyConfigured: boolean;
  apiSecretConfigured: boolean;
  apiPassphraseConfigured: boolean;
  kucoinCopyTradeLeadEnabled?: boolean;
  kucoinCopyTradeFeatureEnabled?: boolean;
  createdAt?: string;
  updatedAt?: string;
}

interface TestResult {
  accountId: number;
  success: boolean;
  message: string;
  responseTimeMs: number;
  error?: string | null;
}

type FormMode = 'create' | 'edit';

const AdminTradingAccounts: React.FC = () => {
  const adminApi = useAdminApiService();

  const [provider, setProvider] = useState<Provider>('KUCOIN');
  const [accounts, setAccounts] = useState<PersonalAccountSummary[]>([]);
  const [loading, setLoading] = useState<boolean>(true);
  const [error, setError] = useState<string | null>(null);
  const [formMode, setFormMode] = useState<FormMode>('create');
  const [editingId, setEditingId] = useState<number | null>(null);
  const [submitting, setSubmitting] = useState<boolean>(false);
  const [testingId, setTestingId] = useState<number | null>(null);
  const [testResult, setTestResult] = useState<TestResult | null>(null);
  const [lastTestAt, setLastTestAt] = useState<Record<number, number>>({});
  /**
   * Lightweight mobile breakpoint awareness so we can tweak inline styles
   * for small screens (e.g. iPhone Safari) without relying on CSS media
   * queries, since inline React styles override stylesheet rules.
   */
  const formRef = useRef<HTMLDivElement>(null);

  const [isMobile, setIsMobile] = useState<boolean>(() => {
    if (typeof window === 'undefined') return false;
    return window.innerWidth <= 640; // Tailwind "sm" breakpoint
  });

  const [form, setForm] = useState({
    name: '',
    apiKey: '',
    apiSecret: '',
    apiPassphrase: '',
    baseUrl: 'https://api.kucoin.com',
    isActive: true,
    kucoinCopyTradeLeadEnabled: false,
  });

  const [formErrors, setFormErrors] = useState<Partial<Record<keyof typeof form, string>>>({});

  useEffect(() => {
    loadAccounts();
  }, []);

  useEffect(() => {
    if (typeof window === 'undefined') return;

    const handleResize = () => {
      setIsMobile(window.innerWidth <= 640);
    };

    window.addEventListener('resize', handleResize);
    return () => window.removeEventListener('resize', handleResize);
  }, []);

  const loadAccounts = async () => {
    try {
      setLoading(true);
      setError(null);
      const response = await adminApi.getMyTradingAccounts();
      if (response.success && response.data) {
        setAccounts(response.data as PersonalAccountSummary[]);
      } else {
        setError(response.error || 'Failed to load personal trading accounts');
      }
    } catch (e: any) {
      console.error('Failed to load personal trading accounts', e);
      setError(e?.message || 'Failed to load personal trading accounts');
    } finally {
      setLoading(false);
    }
  };

  const resetForm = () => {
    setForm({
      name: '',
      apiKey: '',
      apiSecret: '',
      apiPassphrase: '',
      baseUrl: 'https://api.kucoin.com',
      isActive: true,
      kucoinCopyTradeLeadEnabled: false,
    });
    setFormErrors({});
    setEditingId(null);
    setFormMode('create');
  };

  const validateForm = (): boolean => {
    const errors: Partial<Record<keyof typeof form, string>> = {};

    if (!form.name.trim()) {
      errors.name = 'Account name is required';
    }
    if (formMode === 'create') {
      if (!form.apiKey.trim()) {
        errors.apiKey = 'API key is required';
      }
      if (!form.apiSecret.trim()) {
        errors.apiSecret = 'API secret is required';
      }
      if (!form.apiPassphrase.trim()) {
        errors.apiPassphrase = 'API passphrase is required';
      }
    }
    if (form.baseUrl && !/^https?:\/\/.+/i.test(form.baseUrl)) {
      errors.baseUrl = 'Base URL must be a valid http(s) URL';
    }

    setFormErrors(errors);
    return Object.keys(errors).length === 0;
  };

  const handleInputChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const { name, value, type, checked } = e.target;
    setForm(prev => ({
      ...prev,
      [name]: type === 'checkbox' ? checked : value,
    }));
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!validateForm()) return;

    try {
      setSubmitting(true);
      setError(null);

      if (formMode === 'create') {
        const response = await adminApi.createMyTradingAccount({
          name: form.name.trim(),
          apiKey: form.apiKey.trim(),
          apiSecret: form.apiSecret.trim(),
          apiPassphrase: form.apiPassphrase.trim(),
          baseUrl: form.baseUrl.trim(),
          isActive: form.isActive,
          kucoinCopyTradeLeadEnabled: form.kucoinCopyTradeLeadEnabled,
        });
        if (!response.success) {
          setError(response.error || 'Failed to create personal trading account');
        } else {
          resetForm();
          await loadAccounts();
        }
      } else if (formMode === 'edit' && editingId != null) {
        const payload: any = {
          name: form.name.trim(),
          baseUrl: form.baseUrl.trim(),
          isActive: form.isActive,
          kucoinCopyTradeLeadEnabled: form.kucoinCopyTradeLeadEnabled,
        };
        if (form.apiKey.trim()) payload.apiKey = form.apiKey.trim();
        if (form.apiSecret.trim()) payload.apiSecret = form.apiSecret.trim();
        if (form.apiPassphrase.trim()) payload.apiPassphrase = form.apiPassphrase.trim();

        const response = await adminApi.updateMyTradingAccount(editingId, payload);
        if (!response.success) {
          setError(response.error || 'Failed to update personal trading account');
        } else {
          resetForm();
          await loadAccounts();
        }
      }
    } catch (e: any) {
      console.error('Failed to submit personal trading account form', e);
      setError(e?.message || 'Failed to save personal trading account');
    } finally {
      setSubmitting(false);
    }
  };

  const handleEdit = (account: PersonalAccountSummary) => {
    setFormMode('edit');
    setEditingId(account.id);
    setForm({
      name: account.name,
      apiKey: '',
      apiSecret: '',
      apiPassphrase: '',
      baseUrl: account.baseUrl || 'https://api.kucoin.com',
      isActive: account.isActive,
      kucoinCopyTradeLeadEnabled: Boolean(account.kucoinCopyTradeLeadEnabled),
    });
    setFormErrors({});
    // Scroll the form card into view (works inside any scrollable container)
    setTimeout(() => {
      formRef.current?.scrollIntoView({ behavior: 'smooth', block: 'start' });
    }, 50);
  };

  const handleDelete = async (account: PersonalAccountSummary) => {
    const confirmed = window.confirm(
      `Are you sure you want to deactivate personal trading account "${account.name}"?`,
    );
    if (!confirmed) return;

    try {
      setError(null);
      const response = await adminApi.deleteMyTradingAccount(account.id);
      if (!response.success) {
        setError(response.error || 'Failed to deactivate personal trading account');
      } else {
        await loadAccounts();
      }
    } catch (e: any) {
      console.error('Failed to deactivate personal trading account', e);
      setError(e?.message || 'Failed to deactivate personal trading account');
    }
  };

  const handleToggleActive = async (account: PersonalAccountSummary) => {
    try {
      setError(null);
      const response = await adminApi.updateMyTradingAccount(account.id, {
        isActive: !account.isActive,
      });
      if (!response.success) {
        setError(response.error || 'Failed to update account status');
      } else {
        await loadAccounts();
      }
    } catch (e: any) {
      console.error('Failed to update account status', e);
      setError(e?.message || 'Failed to update account status');
    }
  };

  const handleTestConnection = async (account: PersonalAccountSummary) => {
    const now = Date.now();
    const last = lastTestAt[account.id];
    if (last && now - last < 10_000) {
      // Local cooldown matches backend rate limit
      setError('Please wait a few seconds before testing this account again.');
      return;
    }
    try {
      setTestingId(account.id);
      setError(null);
      setTestResult(null);
      const response = await adminApi.testMyTradingAccount(account.id);
      if (response.success && response.data) {
        const data = response.data as TestResult;
        setTestResult(data);
        setLastTestAt(prev => ({ ...prev, [account.id]: now }));
        // auto-clear after 5 seconds
        setTimeout(() => setTestResult(null), 5000);
      } else {
        setError(response.error || 'Failed to test connection');
      }
    } catch (e: any) {
      console.error('Failed to test personal trading account', e);
      setError(e?.message || 'Failed to test connection');
    } finally {
      setTestingId(null);
    }
  };

  const renderStatusBadge = (account: PersonalAccountSummary) => {
    if (account.isActive) {
      return (
        <span style={{
          display: 'inline-flex',
          alignItems: 'center',
          gap: '6px',
          padding: '4px 12px',
          borderRadius: '12px',
          fontSize: '12px',
          fontWeight: 600,
          backgroundColor: '#d1fae5',
          color: '#065f46',
          border: '1px solid #a7f3d0'
        }}>
          <span style={{ width: '6px', height: '6px', borderRadius: '50%', backgroundColor: '#10b981' }}></span>
          Active
        </span>
      );
    }
    return (
      <span style={{
        display: 'inline-flex',
        alignItems: 'center',
        gap: '6px',
        padding: '4px 12px',
        borderRadius: '12px',
        fontSize: '12px',
        fontWeight: 600,
        backgroundColor: '#fee2e2',
        color: '#991b1b',
        border: '1px solid #fecaca'
      }}>
        <span style={{ width: '6px', height: '6px', borderRadius: '50%', backgroundColor: '#ef4444' }}></span>
        Inactive
      </span>
    );
  };

  return (
    <div
      style={{
        padding: isMobile ? '16px 12px 24px' : '24px',
        maxWidth: '1400px',
        margin: '0 auto',
      }}
    >
      {/* Modern Header */}
      <div style={{
        display: 'flex',
        flexDirection: isMobile ? 'column' : 'row',
        justifyContent: 'space-between',
        alignItems: isMobile ? 'flex-start' : 'center',
        gap: isMobile ? '16px' : '0px',
        marginBottom: isMobile ? '24px' : '32px',
        paddingBottom: '20px',
        borderBottom: '2px solid #e5e7eb'
      }}>
        <div>
          <h1 style={{
            margin: 0,
            fontSize: isMobile ? '22px' : '28px',
            fontWeight: 700,
            color: '#111827',
            display: 'flex',
            alignItems: 'center',
            gap: '12px'
          }}>
            <span style={{
              display: 'inline-flex',
              alignItems: 'center',
              justifyContent: 'center',
              width: '40px',
              height: '40px',
              borderRadius: '10px',
              backgroundColor: '#086afb',
              color: 'white',
              fontSize: '20px'
            }}>🔐</span>
            My Trading Accounts
          </h1>
          <p style={{
            margin: '8px 0 0 0',
            fontSize: '14px',
            color: '#6b7280'
          }}>
            {provider === 'KUCOIN'
              ? 'Manage your personal KuCoin API accounts for trading'
              : 'Manage your Trading 212 accounts for the Stock Portfolio'}
          </p>
        </div>
        {provider === 'KUCOIN' && (
        <button
          onClick={loadAccounts}
          disabled={loading}
          style={{
            display: 'inline-flex',
            alignItems: 'center',
            gap: '8px',
            padding: isMobile ? '8px 14px' : '10px 20px',
            backgroundColor: '#f3f4f6',
            border: '1px solid #d1d5db',
            borderRadius: '8px',
            fontSize: '14px',
            fontWeight: 500,
            color: '#374151',
            cursor: loading ? 'not-allowed' : 'pointer',
            transition: 'all 0.2s',
            opacity: loading ? 0.6 : 1,
            alignSelf: isMobile ? 'stretch' : 'auto'
          }}
          onMouseEnter={(e) => {
            if (!loading) {
              e.currentTarget.style.backgroundColor = '#e5e7eb';
            }
          }}
          onMouseLeave={(e) => {
            if (!loading) {
              e.currentTarget.style.backgroundColor = '#f3f4f6';
            }
          }}
        >
          <span>🔄</span>
          Refresh
        </button>
        )}
      </div>

      {/* Provider selector */}
      <div style={{
        display: 'flex',
        gap: '8px',
        marginBottom: isMobile ? '20px' : '28px',
        flexWrap: 'wrap'
      }}>
        {([
          { id: 'KUCOIN' as Provider, label: 'KuCoin (crypto futures)' },
          { id: 'TRADING212' as Provider, label: 'Trading 212 (stocks & ETFs)' },
        ]).map(opt => (
          <button
            key={opt.id}
            type="button"
            onClick={() => setProvider(opt.id)}
            style={{
              padding: '10px 18px',
              borderRadius: '999px',
              fontSize: '14px',
              fontWeight: 600,
              cursor: 'pointer',
              border: provider === opt.id ? '2px solid #086afb' : '1px solid #d1d5db',
              backgroundColor: provider === opt.id ? '#eff6ff' : 'white',
              color: provider === opt.id ? '#086afb' : '#374151',
              transition: 'all 0.2s',
              flex: isMobile ? 1 : 'none'
            }}
          >
            {opt.label}
          </button>
        ))}
      </div>

      {provider === 'TRADING212' && (
        <Trading212AccountsSection isMobile={isMobile} />
      )}

      {provider === 'KUCOIN' && (
      <>
      {/* Modern Security Panel */}
      <div style={{
        marginBottom: isMobile ? '24px' : '32px',
        background: 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)',
        borderRadius: '16px',
        padding: isMobile ? '20px 16px' : '24px',
        color: 'white',
        boxShadow: '0 10px 25px rgba(102, 126, 234, 0.15)'
      }}>
        <div style={{
          display: 'flex',
          flexDirection: isMobile ? 'column' : 'row',
          alignItems: isMobile ? 'flex-start' : 'center',
          gap: '12px',
          marginBottom: '20px'
        }}>
          <div style={{
            width: '48px',
            height: '48px',
            borderRadius: '12px',
            backgroundColor: 'rgba(255, 255, 255, 0.2)',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            fontSize: '24px',
            backdropFilter: 'blur(10px)'
          }}>
            🔒
          </div>
          <div>
            <h3 style={{
              margin: 0,
              fontSize: '20px',
              fontWeight: 700,
              color: 'white'
            }}>
              Security Best Practices
            </h3>
            <p style={{
              margin: '4px 0 0 0',
              fontSize: '14px',
              color: 'rgba(255, 255, 255, 0.9)'
            }}>
              Protect your API credentials and trading accounts
            </p>
          </div>
        </div>
        
        <div style={{
          display: 'grid',
          gridTemplateColumns: 'repeat(auto-fit, minmax(220px, 1fr))',
          gap: '12px',
          marginBottom: '20px'
        }}>
          {[
            'Never share your KuCoin API credentials with anyone',
            'Use IP whitelisting on your KuCoin API keys whenever possible',
            'Disable withdrawal permissions on API keys used with this platform',
            'Rotate API credentials regularly and delete unused keys',
            'Use strong, unique passphrases and store them securely'
          ].map((tip, idx) => (
            <div key={idx} style={{
              display: 'flex',
              alignItems: 'flex-start',
              gap: '10px',
              padding: '12px',
              backgroundColor: 'rgba(255, 255, 255, 0.1)',
              borderRadius: '8px',
              backdropFilter: 'blur(10px)'
            }}>
              <span style={{ fontSize: '16px', flexShrink: 0 }}>✓</span>
              <span style={{ fontSize: '13px', lineHeight: '1.5', color: 'rgba(255, 255, 255, 0.95)' }}>
                {tip}
              </span>
            </div>
          ))}
        </div>

        {/* Help Resources */}
        <div style={{
          display: 'flex',
          flexWrap: 'wrap',
          gap: '12px',
          marginTop: '20px',
          paddingTop: '20px',
          borderTop: '1px solid rgba(255, 255, 255, 0.2)'
        }}>
          {[
            { icon: '📚', label: 'API Docs', href: 'https://docs.kucoin.com/#api' },
            { icon: '🔑', label: 'Create API Keys', href: 'https://www.kucoin.com/support/articles/360015102174' },
            { icon: '🛡️', label: 'Security Guide', href: 'https://www.kucoin.com/support/articles/360015102174' }
          ].map((link, idx) => (
            <a
              key={idx}
              href={link.href}
              target="_blank"
              rel="noopener noreferrer"
              style={{
                display: 'inline-flex',
                alignItems: 'center',
                gap: '6px',
                padding: '8px 14px',
                backgroundColor: 'rgba(255, 255, 255, 0.15)',
                borderRadius: '8px',
                fontSize: '13px',
                fontWeight: 500,
                color: 'white',
                textDecoration: 'none',
                transition: 'all 0.2s',
                border: '1px solid rgba(255, 255, 255, 0.2)'
              }}
              onMouseEnter={(e) => {
                e.currentTarget.style.backgroundColor = 'rgba(255, 255, 255, 0.25)';
                e.currentTarget.style.transform = 'translateY(-2px)';
              }}
              onMouseLeave={(e) => {
                e.currentTarget.style.backgroundColor = 'rgba(255, 255, 255, 0.15)';
                e.currentTarget.style.transform = 'translateY(0)';
              }}
            >
              <span>{link.icon}</span>
              <span>{link.label}</span>
            </a>
          ))}
        </div>
      </div>

      {/* Error Message */}
      {error && (
        <div style={{
          marginBottom: '24px',
          padding: '16px 20px',
          backgroundColor: '#fef2f2',
          border: '1px solid #fecaca',
          borderRadius: '12px',
          display: 'flex',
          alignItems: 'center',
          gap: '12px',
          color: '#991b1b'
        }}>
          <span style={{ fontSize: '20px' }}>⚠️</span>
          <span style={{ flex: 1, fontSize: '14px', fontWeight: 500 }}>{error}</span>
          <button
            onClick={() => setError(null)}
            style={{
              background: 'none',
              border: 'none',
              fontSize: '24px',
              color: '#991b1b',
              cursor: 'pointer',
              padding: 0,
              width: '24px',
              height: '24px',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              borderRadius: '4px',
              transition: 'background 0.2s'
            }}
            onMouseEnter={(e) => {
              e.currentTarget.style.backgroundColor = 'rgba(153, 27, 27, 0.1)';
            }}
            onMouseLeave={(e) => {
              e.currentTarget.style.backgroundColor = 'transparent';
            }}
          >
            ×
          </button>
        </div>
      )}

      {/* Modern Account Form Card */}
      <div ref={formRef} style={{
        marginBottom: isMobile ? '24px' : '32px',
        backgroundColor: 'white',
        borderRadius: '16px',
        border: formMode === 'edit' ? '2px solid #f59e0b' : '1px solid #e5e7eb',
        boxShadow: formMode === 'edit'
          ? '0 4px 6px -1px rgba(245, 158, 11, 0.2), 0 2px 4px -1px rgba(245, 158, 11, 0.1)'
          : '0 4px 6px -1px rgba(0, 0, 0, 0.1), 0 2px 4px -1px rgba(0, 0, 0, 0.06)',
        overflow: 'hidden',
        transition: 'border 0.3s, box-shadow 0.3s'
      }}>
        <div style={{
          padding: isMobile ? '16px' : '24px',
          borderBottom: '1px solid #e5e7eb',
          backgroundColor: '#f9fafb'
        }}>
          <div style={{
            display: 'flex',
            alignItems: 'center',
            gap: '12px',
            marginBottom: '8px'
          }}>
            <div style={{
              width: '40px',
              height: '40px',
              borderRadius: '10px',
              backgroundColor: formMode === 'create' ? '#086afb' : '#f59e0b',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              color: 'white',
              fontSize: '20px',
              fontWeight: 600
            }}>
              {formMode === 'create' ? '+' : '✏️'}
            </div>
            <div>
              <h3 style={{
                margin: 0,
                fontSize: '20px',
                fontWeight: 700,
                color: '#111827'
              }}>
                {formMode === 'create' ? 'Add New Account' : 'Edit Account'}
              </h3>
              <p style={{
                margin: '4px 0 0 0',
                fontSize: '14px',
                color: '#6b7280'
              }}>
                {formMode === 'create' 
                  ? 'Connect your personal KuCoin API account for trading'
                  : 'Update your account settings and credentials'}
              </p>
            </div>
          </div>
        </div>
        <form onSubmit={handleSubmit} style={{ padding: isMobile ? '16px' : '24px' }}>
          <div style={{
            display: 'grid',
            gridTemplateColumns: `repeat(auto-fit, minmax(${isMobile ? 260 : 300}px, 1fr))`,
            gap: isMobile ? '16px' : '20px',
            marginBottom: '24px'
          }}>
            {/* Account Name */}
            <div>
              <label style={{
                display: 'block',
                marginBottom: '8px',
                fontSize: '14px',
                fontWeight: 600,
                color: '#374151'
              }}>
                Account Name <span style={{ color: '#ef4444' }}>*</span>
              </label>
              <input
                type="text"
                name="name"
                value={form.name}
                onChange={handleInputChange}
                placeholder="My Personal KuCoin Account"
                maxLength={100}
                style={{
                  width: '100%',
                  padding: '12px 16px',
                  border: formErrors.name ? '2px solid #ef4444' : '1px solid #d1d5db',
                  borderRadius: '8px',
                  fontSize: '14px',
                  transition: 'all 0.2s',
                  outline: 'none'
                }}
                onFocus={(e) => {
                  e.currentTarget.style.borderColor = '#086afb';
                  e.currentTarget.style.boxShadow = '0 0 0 3px rgba(8, 106, 251, 0.1)';
                }}
                onBlur={(e) => {
                  e.currentTarget.style.borderColor = formErrors.name ? '#ef4444' : '#d1d5db';
                  e.currentTarget.style.boxShadow = 'none';
                }}
              />
              {formErrors.name && (
                <div style={{ marginTop: '6px', fontSize: '12px', color: '#ef4444' }}>
                  {formErrors.name}
                </div>
              )}
            </div>

            {/* Base URL */}
            <div>
              <label style={{
                display: 'block',
                marginBottom: '8px',
                fontSize: '14px',
                fontWeight: 600,
                color: '#374151'
              }}>
                Base URL
              </label>
              <input
                type="text"
                name="baseUrl"
                value={form.baseUrl}
                onChange={handleInputChange}
                placeholder="https://api.kucoin.com"
                style={{
                  width: '100%',
                  padding: '12px 16px',
                  border: formErrors.baseUrl ? '2px solid #ef4444' : '1px solid #d1d5db',
                  borderRadius: '8px',
                  fontSize: '14px',
                  transition: 'all 0.2s',
                  outline: 'none'
                }}
                onFocus={(e) => {
                  e.currentTarget.style.borderColor = '#086afb';
                  e.currentTarget.style.boxShadow = '0 0 0 3px rgba(8, 106, 251, 0.1)';
                }}
                onBlur={(e) => {
                  e.currentTarget.style.borderColor = formErrors.baseUrl ? '#ef4444' : '#d1d5db';
                  e.currentTarget.style.boxShadow = 'none';
                }}
              />
              {formErrors.baseUrl && (
                <div style={{ marginTop: '6px', fontSize: '12px', color: '#ef4444' }}>
                  {formErrors.baseUrl}
                </div>
              )}
            </div>
          </div>

          {/* Credentials Section */}
          <div style={{
            marginBottom: '24px',
            padding: isMobile ? '16px' : '20px',
            backgroundColor: '#f9fafb',
            borderRadius: '12px',
            border: '1px solid #e5e7eb'
          }}>
            <h4 style={{
              margin: '0 0 16px 0',
              fontSize: '16px',
              fontWeight: 600,
              color: '#111827',
              display: 'flex',
              alignItems: 'center',
              gap: '8px'
            }}>
              <span>🔑</span>
              API Credentials
            </h4>

            {/* Security notice in edit mode */}
            {formMode === 'edit' && (
              <div style={{
                marginBottom: '16px',
                padding: '12px 16px',
                backgroundColor: '#fffbeb',
                border: '1px solid #fcd34d',
                borderRadius: '8px',
                display: 'flex',
                alignItems: 'flex-start',
                gap: '10px'
              }}>
                <span style={{ fontSize: '18px', flexShrink: 0 }}>🔒</span>
                <div>
                  <p style={{ margin: '0 0 4px 0', fontSize: '13px', fontWeight: 600, color: '#92400e' }}>
                    API credentials are stored securely and never shown again
                  </p>
                  <p style={{ margin: 0, fontSize: '12px', color: '#b45309' }}>
                    Leave the fields below <strong>blank</strong> to keep your existing credentials unchanged.
                    Only fill in a field if you want to <strong>replace</strong> it with a new value.
                  </p>
                </div>
              </div>
            )}
          <div style={{
            display: 'grid',
            gridTemplateColumns: `repeat(auto-fit, minmax(${isMobile ? 260 : 300}px, 1fr))`,
            gap: isMobile ? '16px' : '20px'
          }}>
            {/* API Key */}
            <div>
              <label style={{
                display: 'block',
                marginBottom: '8px',
                fontSize: '14px',
                fontWeight: 600,
                color: '#374151'
              }}>
                API Key {formMode === 'create' && <span style={{ color: '#ef4444' }}>*</span>}
              </label>
              <input
                type="password"
                name="apiKey"
                value={form.apiKey}
                onChange={handleInputChange}
                placeholder={formMode === 'edit' ? 'Leave blank to keep existing' : 'Enter your KuCoin API key'}
                style={{
                  width: '100%',
                  padding: '12px 16px',
                  border: formErrors.apiKey ? '2px solid #ef4444' : '1px solid #d1d5db',
                  borderRadius: '8px',
                  fontSize: '14px',
                  transition: 'all 0.2s',
                  outline: 'none',
                  fontFamily: 'monospace'
                }}
                onFocus={(e) => {
                  e.currentTarget.style.borderColor = '#086afb';
                  e.currentTarget.style.boxShadow = '0 0 0 3px rgba(8, 106, 251, 0.1)';
                }}
                onBlur={(e) => {
                  e.currentTarget.style.borderColor = formErrors.apiKey ? '#ef4444' : '#d1d5db';
                  e.currentTarget.style.boxShadow = 'none';
                }}
              />
              {formErrors.apiKey && (
                <div style={{ marginTop: '6px', fontSize: '12px', color: '#ef4444' }}>
                  {formErrors.apiKey}
                </div>
              )}
            </div>

            {/* API Secret */}
            <div>
              <label style={{
                display: 'block',
                marginBottom: '8px',
                fontSize: '14px',
                fontWeight: 600,
                color: '#374151'
              }}>
                API Secret {formMode === 'create' && <span style={{ color: '#ef4444' }}>*</span>}
              </label>
              <input
                type="password"
                name="apiSecret"
                value={form.apiSecret}
                onChange={handleInputChange}
                placeholder={formMode === 'edit' ? 'Leave blank to keep existing' : 'Enter your KuCoin API secret'}
                style={{
                  width: '100%',
                  padding: '12px 16px',
                  border: formErrors.apiSecret ? '2px solid #ef4444' : '1px solid #d1d5db',
                  borderRadius: '8px',
                  fontSize: '14px',
                  transition: 'all 0.2s',
                  outline: 'none',
                  fontFamily: 'monospace'
                }}
                onFocus={(e) => {
                  e.currentTarget.style.borderColor = '#086afb';
                  e.currentTarget.style.boxShadow = '0 0 0 3px rgba(8, 106, 251, 0.1)';
                }}
                onBlur={(e) => {
                  e.currentTarget.style.borderColor = formErrors.apiSecret ? '#ef4444' : '#d1d5db';
                  e.currentTarget.style.boxShadow = 'none';
                }}
              />
              {formErrors.apiSecret && (
                <div style={{ marginTop: '6px', fontSize: '12px', color: '#ef4444' }}>
                  {formErrors.apiSecret}
                </div>
              )}
            </div>

            {/* API Passphrase */}
            <div>
              <label style={{
                display: 'block',
                marginBottom: '8px',
                fontSize: '14px',
                fontWeight: 600,
                color: '#374151'
              }}>
                API Passphrase {formMode === 'create' && <span style={{ color: '#ef4444' }}>*</span>}
              </label>
              <input
                type="password"
                name="apiPassphrase"
                value={form.apiPassphrase}
                onChange={handleInputChange}
                placeholder={formMode === 'edit' ? 'Leave blank to keep existing' : 'Enter your KuCoin passphrase'}
                style={{
                  width: '100%',
                  padding: '12px 16px',
                  border: formErrors.apiPassphrase ? '2px solid #ef4444' : '1px solid #d1d5db',
                  borderRadius: '8px',
                  fontSize: '14px',
                  transition: 'all 0.2s',
                  outline: 'none',
                  fontFamily: 'monospace'
                }}
                onFocus={(e) => {
                  e.currentTarget.style.borderColor = '#086afb';
                  e.currentTarget.style.boxShadow = '0 0 0 3px rgba(8, 106, 251, 0.1)';
                }}
                onBlur={(e) => {
                  e.currentTarget.style.borderColor = formErrors.apiPassphrase ? '#ef4444' : '#d1d5db';
                  e.currentTarget.style.boxShadow = 'none';
                }}
              />
              {formErrors.apiPassphrase && (
                <div style={{ marginTop: '6px', fontSize: '12px', color: '#ef4444' }}>
                  {formErrors.apiPassphrase}
                </div>
              )}
            </div>
          </div>
          </div>

          {/* Active Toggle */}
          <div style={{
            marginBottom: '24px',
            padding: isMobile ? '12px' : '16px',
            backgroundColor: '#f9fafb',
            borderRadius: '8px',
            border: '1px solid #e5e7eb'
          }}>
            <label style={{
              display: 'flex',
              alignItems: 'center',
              gap: '12px',
              cursor: 'pointer',
              fontSize: '14px',
              fontWeight: 500,
              color: '#374151'
            }}>
              <input
                type="checkbox"
                name="isActive"
                checked={form.isActive}
                onChange={handleInputChange}
                style={{
                  width: '20px',
                  height: '20px',
                  cursor: 'pointer',
                  accentColor: '#086afb'
                }}
              />
              <span>Enable this account for trading</span>
            </label>
            <label style={{
              display: 'flex',
              alignItems: 'flex-start',
              gap: '12px',
              cursor: 'pointer',
              fontSize: '14px',
              fontWeight: 500,
              color: '#374151',
              marginTop: 14,
            }}>
              <input
                type="checkbox"
                name="kucoinCopyTradeLeadEnabled"
                checked={form.kucoinCopyTradeLeadEnabled}
                onChange={handleInputChange}
                style={{
                  width: '20px',
                  height: '20px',
                  cursor: 'pointer',
                  accentColor: '#e7038e',
                  marginTop: 2,
                  flexShrink: 0,
                }}
              />
              <span>
                KuCoin Copy Trading lead account
                <span style={{ display: 'block', marginTop: 4, fontSize: 12, fontWeight: 400, color: '#6b7280', lineHeight: 1.45 }}>
                  When on (and server flag trading.kucoin.copy-trade.enabled=true), futures opens/closes
                  use KuCoin lead-trader APIs so KuCoin followers can copy this account. Use a dedicated
                  lead API key with LeadtradeFutures permission — do not enable on everyday bot accounts.
                </span>
              </span>
            </label>
          </div>

          {/* Form Actions */}
          <div style={{
            display: 'flex',
            flexDirection: isMobile ? 'column-reverse' : 'row',
            gap: '12px',
            justifyContent: isMobile ? 'stretch' : 'flex-end',
            paddingTop: '20px',
            borderTop: '1px solid #e5e7eb'
          }}>
            {formMode === 'edit' && (
              <button
                type="button"
                onClick={resetForm}
                disabled={submitting}
                style={{
                  padding: '12px 24px',
                  backgroundColor: '#f3f4f6',
                  border: '1px solid #d1d5db',
                  borderRadius: '8px',
                  fontSize: '14px',
                  fontWeight: 600,
                  color: '#374151',
                  cursor: submitting ? 'not-allowed' : 'pointer',
                  transition: 'all 0.2s',
                  opacity: submitting ? 0.6 : 1
                }}
                onMouseEnter={(e) => {
                  if (!submitting) {
                    e.currentTarget.style.backgroundColor = '#e5e7eb';
                  }
                }}
                onMouseLeave={(e) => {
                  if (!submitting) {
                    e.currentTarget.style.backgroundColor = '#f3f4f6';
                  }
                }}
              >
                Cancel
              </button>
            )}
            <button
              type="submit"
              disabled={submitting}
              style={{
                padding: '12px 24px',
                backgroundColor: '#086afb',
                border: 'none',
                borderRadius: '8px',
                fontSize: '14px',
                fontWeight: 600,
                color: 'white',
                cursor: submitting ? 'not-allowed' : 'pointer',
                transition: 'all 0.2s',
                opacity: submitting ? 0.6 : 1,
                boxShadow: '0 2px 4px rgba(8, 106, 251, 0.2)'
              }}
              onMouseEnter={(e) => {
                if (!submitting) {
                  e.currentTarget.style.backgroundColor = '#0759d1';
                  e.currentTarget.style.boxShadow = '0 4px 8px rgba(8, 106, 251, 0.3)';
                  e.currentTarget.style.transform = 'translateY(-1px)';
                }
              }}
              onMouseLeave={(e) => {
                if (!submitting) {
                  e.currentTarget.style.backgroundColor = '#086afb';
                  e.currentTarget.style.boxShadow = '0 2px 4px rgba(8, 106, 251, 0.2)';
                  e.currentTarget.style.transform = 'translateY(0)';
                }
              }}
            >
              {submitting
                ? (formMode === 'create' ? 'Creating...' : 'Saving...')
                : (formMode === 'create' ? 'Create Account' : 'Save Changes')}
            </button>
          </div>
        </form>
      </div>

      {/* Test result banner */}
      {testResult && (
        <div style={{
          marginBottom: '24px',
          padding: '16px 20px',
          backgroundColor: testResult.success ? '#f0fdf4' : '#fef2f2',
          border: `1px solid ${testResult.success ? '#bbf7d0' : '#fecaca'}`,
          borderRadius: '12px',
          display: 'flex',
          alignItems: 'center',
          gap: '12px',
          color: testResult.success ? '#166534' : '#991b1b'
        }}>
          <span style={{ fontSize: '20px' }}>{testResult.success ? '✓' : '⚠️'}</span>
          <span style={{ flex: 1, fontSize: '14px', fontWeight: 500 }}>
            {testResult.message} ({testResult.responseTimeMs} ms)
            {testResult.error && ` – ${testResult.error}`}
          </span>
        </div>
      )}

      {/* Accounts Section */}
      {loading ? (
        <div style={{
          display: 'flex',
          flexDirection: 'column',
          alignItems: 'center',
          justifyContent: 'center',
          padding: isMobile ? '40px 16px' : '60px 20px',
          backgroundColor: 'white',
          borderRadius: '16px',
          border: '1px solid #e5e7eb'
        }}>
          <div style={{
            width: '48px',
            height: '48px',
            border: '4px solid #f3f4f6',
            borderTopColor: '#086afb',
            borderRadius: '50%',
            animation: 'spin 1s linear infinite',
            marginBottom: '16px'
          }}></div>
          <p style={{ margin: 0, fontSize: '14px', color: '#6b7280', fontWeight: 500 }}>
            Loading personal accounts...
          </p>
        </div>
      ) : accounts.length === 0 ? (
        <div style={{
          padding: isMobile ? '40px 20px' : '60px 40px',
          backgroundColor: 'white',
          borderRadius: '16px',
          border: '2px dashed #d1d5db',
          textAlign: 'center'
        }}>
          <div style={{
            width: isMobile ? '64px' : '80px',
            height: isMobile ? '64px' : '80px',
            margin: '0 auto 24px',
            borderRadius: '50%',
            backgroundColor: '#f3f4f6',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            fontSize: '40px'
          }}>
            📭
          </div>
          <h3 style={{
            margin: '0 0 12px 0',
            fontSize: '24px',
            fontWeight: 700,
            color: '#111827'
          }}>
            No Personal Accounts
          </h3>
          <p style={{
            margin: '0 0 8px 0',
            fontSize: '16px',
            color: '#6b7280'
          }}>
            You don&apos;t have any personal KuCoin trading accounts yet.
          </p>
          <p style={{
            margin: '0 0 24px 0',
            fontSize: '14px',
            color: '#9ca3af'
          }}>
            Use the form above to add your first account and start trading with your own API keys.
          </p>
          <a
            href="../../../../docs/ADMIN_PERSONAL_TRADING_ACCOUNTS_GUIDE.md"
            target="_blank"
            rel="noopener noreferrer"
            style={{
              display: 'inline-flex',
              alignItems: 'center',
              gap: '8px',
              padding: '12px 24px',
              backgroundColor: '#f3f4f6',
              border: '1px solid #d1d5db',
              borderRadius: '8px',
              fontSize: '14px',
              fontWeight: 600,
              color: '#374151',
              textDecoration: 'none',
              transition: 'all 0.2s'
            }}
            onMouseEnter={(e) => {
              e.currentTarget.style.backgroundColor = '#e5e7eb';
              e.currentTarget.style.transform = 'translateY(-2px)';
            }}
            onMouseLeave={(e) => {
              e.currentTarget.style.backgroundColor = '#f3f4f6';
              e.currentTarget.style.transform = 'translateY(0)';
            }}
          >
            <span>📖</span>
            <span>View Setup Guide</span>
          </a>
        </div>
      ) : (
        <div>
          <div style={{
            display: 'flex',
            flexDirection: isMobile ? 'column' : 'row',
            justifyContent: isMobile ? 'flex-start' : 'space-between',
            alignItems: isMobile ? 'flex-start' : 'center',
            gap: isMobile ? '8px' : '0px',
            marginBottom: '20px'
          }}>
            <h3 style={{
              margin: 0,
              fontSize: '20px',
              fontWeight: 700,
              color: '#111827'
            }}>
              Your Accounts ({accounts.length})
            </h3>
          </div>
          
          <div style={{
            display: 'grid',
            gridTemplateColumns: isMobile ? '1fr' : 'repeat(auto-fill, minmax(400px, 1fr))',
            gap: '16px'
          }}>
            {accounts.map(account => (
              <div
                key={account.id}
                style={{
                  backgroundColor: 'white',
                  borderRadius: '16px',
                  border: '1px solid #e5e7eb',
                  padding: isMobile ? '18px 14px' : '24px',
                  boxShadow: '0 2px 4px rgba(0, 0, 0, 0.05)',
                  transition: 'all 0.2s',
                  position: 'relative',
                  overflow: 'hidden'
                }}
                onMouseEnter={(e) => {
                  e.currentTarget.style.boxShadow = '0 8px 16px rgba(0, 0, 0, 0.1)';
                  e.currentTarget.style.transform = 'translateY(-2px)';
                }}
                onMouseLeave={(e) => {
                  e.currentTarget.style.boxShadow = '0 2px 4px rgba(0, 0, 0, 0.05)';
                  e.currentTarget.style.transform = 'translateY(0)';
                }}
              >
                {/* Status indicator bar */}
                <div style={{
                  position: 'absolute',
                  top: 0,
                  left: 0,
                  right: 0,
                  height: '4px',
                  backgroundColor: account.isActive ? '#10b981' : '#ef4444'
                }}></div>

                {/* Account Header */}
                <div style={{
                  display: 'flex',
                  flexDirection: isMobile ? 'column' : 'row',
                  justifyContent: 'space-between',
                  alignItems: isMobile ? 'flex-start' : 'flex-start',
                  gap: isMobile ? '12px' : '0px',
                  marginBottom: '20px'
                }}>
                  <div style={{ flex: 1 }}>
                    <div style={{
                      display: 'flex',
                      alignItems: 'center',
                      gap: '12px',
                      marginBottom: '8px'
                    }}>
                      <div style={{
                        width: '48px',
                        height: '48px',
                        borderRadius: '12px',
                        backgroundColor: '#086afb',
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'center',
                        color: 'white',
                        fontSize: '24px',
                        fontWeight: 600
                      }}>
                        🔐
                      </div>
                      <div style={{ flex: 1 }}>
                        <h4 style={{
                          margin: 0,
                          fontSize: '18px',
                          fontWeight: 700,
                          color: '#111827'
                        }}>
                          {account.name}
                        </h4>
                        <p style={{
                          margin: '4px 0 0 0',
                          fontSize: '12px',
                          color: '#6b7280',
                          textTransform: 'uppercase',
                          letterSpacing: '0.5px',
                          fontWeight: 600
                        }}>
                          KUCOIN
                          {account.kucoinCopyTradeLeadEnabled && (
                            <span style={{
                              marginLeft: 8,
                              padding: '2px 8px',
                              borderRadius: 999,
                              background: account.kucoinCopyTradeFeatureEnabled ? '#fce7f3' : '#f3f4f6',
                              color: account.kucoinCopyTradeFeatureEnabled ? '#9d174d' : '#6b7280',
                              fontSize: 10,
                              fontWeight: 700,
                              letterSpacing: 0,
                              textTransform: 'none',
                            }}>
                              {account.kucoinCopyTradeFeatureEnabled ? 'Copy-trade lead (active)' : 'Copy-trade lead (flag off)'}
                            </span>
                          )}
                        </p>
                      </div>
                    </div>
                  </div>
                  {renderStatusBadge(account)}
                </div>

                {/* Account Details */}
                <div style={{
                  display: 'flex',
                  flexDirection: 'column',
                  gap: '12px',
                  marginBottom: '20px',
                  padding: isMobile ? '12px' : '16px',
                  backgroundColor: '#f9fafb',
                  borderRadius: '8px'
                }}>
                  <div style={{
                    display: 'flex',
                    justifyContent: 'space-between',
                    alignItems: 'center',
                    gap: '8px'
                  }}>
                    <span style={{ fontSize: '13px', color: '#6b7280', fontWeight: 500 }}>
                      Base URL:
                    </span>
                    <span style={{ fontSize: '13px', color: '#111827', fontFamily: 'monospace' }}>
                      {account.baseUrl || 'Default'}
                    </span>
                  </div>
                  
                  <div style={{
                    display: 'flex',
                    justifyContent: 'space-between',
                    alignItems: 'center'
                  }}>
                    <span style={{ fontSize: '13px', color: '#6b7280', fontWeight: 500 }}>
                      Credentials:
                    </span>
                    <div style={{ display: 'flex', gap: '6px', flexWrap: 'wrap', justifyContent: 'flex-end' }}>
                      {[
                        { label: 'Key', ok: account.apiKeyConfigured },
                        { label: 'Secret', ok: account.apiSecretConfigured },
                        { label: 'Pass', ok: account.apiPassphraseConfigured },
                      ].map(({ label, ok }) => (
                        <span key={label} style={{
                          display: 'inline-flex',
                          alignItems: 'center',
                          gap: '4px',
                          padding: '2px 8px',
                          borderRadius: '9999px',
                          fontSize: '11px',
                          fontWeight: 600,
                          backgroundColor: ok ? '#d1fae5' : '#fee2e2',
                          color: ok ? '#065f46' : '#991b1b',
                          border: `1px solid ${ok ? '#a7f3d0' : '#fecaca'}`
                        }}>
                          {label} {ok ? '✓' : '✗'}
                        </span>
                      ))}
                    </div>
                  </div>

                  {account.createdAt && (
                    <div style={{
                      display: 'flex',
                      justifyContent: 'space-between',
                      alignItems: 'center',
                      paddingTop: '12px',
                      borderTop: '1px solid #e5e7eb'
                    }}>
                      <span style={{ fontSize: '12px', color: '#9ca3af' }}>
                        Created: {new Date(account.createdAt).toLocaleDateString()}
                      </span>
                      {account.updatedAt && (
                        <span style={{ fontSize: '12px', color: '#9ca3af' }}>
                          Updated: {new Date(account.updatedAt).toLocaleDateString()}
                        </span>
                      )}
                    </div>
                  )}
                </div>

                {/* Action Buttons */}
                <div style={{
                  display: 'flex',
                  gap: '8px',
                  flexWrap: 'wrap'
                }}>
                  <button
                    type="button"
                    onClick={() => handleEdit(account)}
                    style={{
                      flex: 1,
                      padding: '10px 16px',
                      backgroundColor: '#f3f4f6',
                      border: '1px solid #d1d5db',
                      borderRadius: '8px',
                      fontSize: '13px',
                      fontWeight: 600,
                      color: '#374151',
                      cursor: 'pointer',
                      transition: 'all 0.2s',
                      display: 'flex',
                      alignItems: 'center',
                      justifyContent: 'center',
                      gap: '6px'
                    }}
                    onMouseEnter={(e) => {
                      e.currentTarget.style.backgroundColor = '#e5e7eb';
                    }}
                    onMouseLeave={(e) => {
                      e.currentTarget.style.backgroundColor = '#f3f4f6';
                    }}
                  >
                    <span>✏️</span>
                    <span>Edit</span>
                  </button>
                  
                  <button
                    type="button"
                    onClick={() => handleTestConnection(account)}
                    disabled={testingId === account.id}
                    style={{
                      flex: 1,
                      padding: '10px 16px',
                      backgroundColor: testingId === account.id ? '#f3f4f6' : '#f0fdf4',
                      border: '1px solid #d1d5db',
                      borderRadius: '8px',
                      fontSize: '13px',
                      fontWeight: 600,
                      color: testingId === account.id ? '#9ca3af' : '#166534',
                      cursor: testingId === account.id ? 'not-allowed' : 'pointer',
                      transition: 'all 0.2s',
                      display: 'flex',
                      alignItems: 'center',
                      justifyContent: 'center',
                      gap: '6px',
                      opacity: testingId === account.id ? 0.6 : 1
                    }}
                    onMouseEnter={(e) => {
                      if (testingId !== account.id) {
                        e.currentTarget.style.backgroundColor = '#dcfce7';
                      }
                    }}
                    onMouseLeave={(e) => {
                      if (testingId !== account.id) {
                        e.currentTarget.style.backgroundColor = '#f0fdf4';
                      }
                    }}
                  >
                    <span>{testingId === account.id ? '⏳' : '🧪'}</span>
                    <span>{testingId === account.id ? 'Testing...' : 'Test'}</span>
                  </button>
                  
                  <button
                    type="button"
                    onClick={() => handleToggleActive(account)}
                    style={{
                      padding: '10px 16px',
                      backgroundColor: account.isActive ? '#fef2f2' : '#f0fdf4',
                      border: `1px solid ${account.isActive ? '#fecaca' : '#bbf7d0'}`,
                      borderRadius: '8px',
                      fontSize: '13px',
                      fontWeight: 600,
                      color: account.isActive ? '#991b1b' : '#166534',
                      cursor: 'pointer',
                      transition: 'all 0.2s',
                      display: 'flex',
                      alignItems: 'center',
                      justifyContent: 'center',
                      gap: '6px'
                    }}
                    onMouseEnter={(e) => {
                      e.currentTarget.style.backgroundColor = account.isActive ? '#fee2e2' : '#dcfce7';
                    }}
                    onMouseLeave={(e) => {
                      e.currentTarget.style.backgroundColor = account.isActive ? '#fef2f2' : '#f0fdf4';
                    }}
                  >
                    <span>{account.isActive ? '⏸️' : '▶️'}</span>
                    <span>{account.isActive ? 'Deactivate' : 'Activate'}</span>
                  </button>
                  
                  <button
                    type="button"
                    onClick={() => handleDelete(account)}
                    style={{
                      padding: '10px 16px',
                      backgroundColor: '#fef2f2',
                      border: '1px solid #fecaca',
                      borderRadius: '8px',
                      fontSize: '13px',
                      fontWeight: 600,
                      color: '#dc2626',
                      cursor: 'pointer',
                      transition: 'all 0.2s',
                      display: 'flex',
                      alignItems: 'center',
                      justifyContent: 'center',
                      gap: '6px'
                    }}
                    onMouseEnter={(e) => {
                      e.currentTarget.style.backgroundColor = '#fee2e2';
                      e.currentTarget.style.borderColor = '#fca5a5';
                    }}
                    onMouseLeave={(e) => {
                      e.currentTarget.style.backgroundColor = '#fef2f2';
                      e.currentTarget.style.borderColor = '#fecaca';
                    }}
                  >
                    <span>🗑️</span>
                    <span>Delete</span>
                  </button>
                </div>
              </div>
            ))}
          </div>
        </div>
      )}
      </>
      )}

      {/* Add CSS animation for spinner */}
      <style>{`
        @keyframes spin {
          to { transform: rotate(360deg); }
        }
      `}</style>
    </div>
  );
};

export default AdminTradingAccounts;

